import { SlideImageOption, UnsplashImage } from "./types";
import { SLIDE_COUNT, PORTRAIT_HINT, GPT_PORTRAIT_SIZE, APP_VISUAL_DESCRIPTION } from "./SlideGenerator.constants";
import { CommunitySlideContext, StoryPromptTemplateMap, StoryPromptTemplateOverrides, StoryType } from "./SlideGenerator.types";
import { buildVoiceOfCustomerPromptBlock } from "../../../../../../database/community/mediaConfig";
import {
  createOpenAIChatCompletion,
  createOpenAIImageGeneration,
  createOpenAIImageEdit,
} from "../../../../../../services/openaiFunctions";

// Prompt logger — surfaces what's being injected into Slide generation so
// you can verify VOC, persona, format profile, and custom formats actually
// make it into the prompt. Always fires.
const logSlidePromptDev = (
  label: string,
  context: CommunitySlideContext | undefined,
  fullPrompt: string,
  extras?: Record<string, unknown>
): void => {
  const vocCount = context?.voiceOfCustomer?.quotes?.length ?? 0;
  const hasPersona = Boolean(context?.buyerPersona?.trim());
  const profileEntries = context?.formatProfile?.formats?.length ?? 0;
  const customFormatKeys = (context?.customFormats || []).map((cf) => cf.key);

  /* eslint-disable no-console */
  console.group(
    `%c[SlideGenerator] ${label}`,
    "color: #ea580c; font-weight: bold; font-size: 12px;"
  );
  console.log("Injected into prompt:", {
    "VOC quotes": vocCount,
    "Buyer persona present": hasPersona,
    "Format profile entries": profileEntries,
    "Custom format keys": customFormatKeys,
    ...(extras || {}),
  });
  console.log("─── Full system prompt ───");
  console.log(fullPrompt);
  console.groupEnd();
  /* eslint-enable no-console */
};

const STORY_PROMPT_BRAND_PLACEHOLDER = "{{BRAND_NAME}}";
const STORY_PROMPT_SLIDE_COUNT_PLACEHOLDER = "{{SLIDE_COUNT}}";

// Returns an empty map. Coach-editable prompt templates have been retired
// (see CommunityMediaConfig.customFormats — formats now come from research).
// Kept as a no-op so legacy callers (e.g. SlideGenerator controller's state
// initializer) still compile.
export const getDefaultStoryPromptTemplates = (): StoryPromptTemplateMap => ({});

/**
 * Returns the slide-by-slide arc instruction for a given format key. The key
 * MUST correspond to a research-discovered custom format saved in
 * mediaConfig.customFormats. There are no hard-coded fallbacks — if no
 * matching custom format exists, this throws.
 */
export const getStoryPromptTemplate = (
  storyType: StoryType,
  _storyPromptTemplates?: StoryPromptTemplateOverrides,
  customFormats?: { key: string; structure: string; label?: string }[]
): string => {
  const custom = customFormats?.find((cf) => cf.key === storyType);
  if (!custom || !custom.structure?.trim()) {
    throw new Error(
      `No format found for key "${String(storyType)}". Run audience + format research first so the system has formats to use.`
    );
  }
  const label = custom.label || String(storyType);
  return `THE FORMAT — "${label}" ({{SLIDE_COUNT}} slides):\n\n${custom.structure.trim()}\n\nReturn ONLY a JSON array of exactly {{SLIDE_COUNT}} strings — one per slide, no markdown.`;
};

const getBrandName = (communityContext?: CommunitySlideContext): string => {
  const title = communityContext?.title?.trim();
  return title || "this community";
};

const buildCommunityContextNote = (communityContext?: CommunitySlideContext): string => {
  const details = [
    communityContext?.title?.trim() ? `Community name: ${communityContext.title.trim()}` : "",
    communityContext?.description?.trim() ? `Description: ${communityContext.description.trim()}` : "",
    communityContext?.shortDescription?.trim() ? `Short description: ${communityContext.shortDescription.trim()}` : "",
    communityContext?.categoryName?.trim() ? `Category: ${communityContext.categoryName.trim()}` : "",
    communityContext?.tags?.length ? `Tags: ${communityContext.tags.join(", ")}` : "",
  ].filter(Boolean);

  const buyerPersonaNote = communityContext?.buyerPersona?.trim()
    ? `\nTarget audience (buyer persona): ${communityContext.buyerPersona.trim()}. Write every hook as if you are speaking directly to this specific person — their fears, frustrations, and desires. The hook should make them feel like this post was made just for them.`
    : "";

  const vocBlock = buildVoiceOfCustomerPromptBlock(communityContext?.voiceOfCustomer);
  const vocNote = vocBlock ? `\n\n${vocBlock}\n\nWhen possible, reuse the EXACT phrasing from the audience-voice list above in slide titles and CTAs. The audience should hear themselves in this carousel.` : "";

  if (!details.length) {
    return `Community context: none provided. Keep the language universal and do not assume business, creator, or spiritual framing unless the lesson itself clearly points there.${buyerPersonaNote}${vocNote}`;
  }

  return `Community context:
- ${details.join("\n- ")}

Use this context to choose the worldview, examples, metaphors, vocabulary, stakes, and CTA. If the community sounds like Digital Product University, business-building language can fit. If it sounds like Buddha Teachings, use teachings, practice, inner conflict, compassion, discipline, or historical/spiritual framing where appropriate.${buyerPersonaNote}${vocNote}`;
};

const formatLabelFor = (
  storyType: StoryType,
  customFormats?: CommunitySlideContext["customFormats"]
): string => {
  const custom = customFormats?.find((cf) => cf.key === storyType);
  if (custom) return custom.label;
  // Built-in: just title-case the key.
  return String(storyType)
    .split("-")
    .map((part) => (part.length ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
};

const buildAudienceBlock = (
  communityContext?: CommunitySlideContext
): string => {
  const persona = communityContext?.buyerPersona?.trim();
  const quotes = (communityContext?.voiceOfCustomer?.quotes || [])
    .filter((q) => q.text?.trim())
    .slice(0, 5);

  if (!persona && quotes.length === 0) return "";

  const lines: string[] = ["THE AUDIENCE:"];
  if (persona) {
    const trimmed =
      persona.length > 320 ? persona.slice(0, 320).trim() + "..." : persona;
    lines.push(trimmed);
  }
  if (quotes.length) {
    lines.push("They say things like:");
    quotes.forEach((q) => {
      const tag = q.tag ? `[${q.tag}] ` : "";
      lines.push(`- ${tag}"${q.text.trim()}"`);
    });
  }
  return lines.join("\n");
};

const buildCommunityShortContext = (
  communityContext?: CommunitySlideContext
): string => {
  const bits: string[] = [];
  if (communityContext?.description?.trim()) {
    const desc = communityContext.description.trim();
    bits.push(`What it's about: ${desc.length > 200 ? desc.slice(0, 200) + "..." : desc}`);
  } else if (communityContext?.shortDescription?.trim()) {
    bits.push(`What it's about: ${communityContext.shortDescription.trim()}`);
  }
  if (communityContext?.categoryName?.trim()) {
    bits.push(`Category: ${communityContext.categoryName.trim()}`);
  }
  return bits.join("\n");
};

const buildSystemPrompt = (
  storyType: StoryType,
  styleNote: string,
  communityContext?: CommunitySlideContext,
  storyPromptTemplates?: StoryPromptTemplateOverrides
): string => {
  const brandName = getBrandName(communityContext);
  const formatLabel = formatLabelFor(storyType, communityContext?.customFormats);
  const audienceBlock = buildAudienceBlock(communityContext);
  const contextLine = buildCommunityShortContext(communityContext);

  const formatArc = getStoryPromptTemplate(
    storyType,
    storyPromptTemplates,
    communityContext?.customFormats
  )
    .split(STORY_PROMPT_BRAND_PLACEHOLDER).join(brandName)
    .split(STORY_PROMPT_SLIDE_COUNT_PLACEHOLDER).join(String(SLIDE_COUNT));

  const styleLine = styleNote.trim() ? `\n${styleNote.trim()}` : "";

  return `You're writing a "${formatLabel}" Instagram carousel (${SLIDE_COUNT} slides) for ${brandName}.

${contextLine ? contextLine + "\n\n" : ""}${audienceBlock ? audienceBlock + "\n\n" : ""}THE FORMAT:
${formatArc}

RULES:
- Voice/POV: follow whatever the FORMAT block above specifies (first person, second person, AI persona, omniscient, etc.). If unspecified, default to first person ("I", "my").
- 4th-grade reading level. No jargon.
- 8-14 words per slide. Slide 1 max 10 words.
- Active voice. Cut filler: "really", "actually", "just", "very", "literally".
- Reuse the audience's exact phrasing when it fits — they should hear themselves.
- One slide MUST be screenshotable on its own (the save).
- The final slide MUST be a universal truth a friend would forward (the share).
- Mention "${brandName}" ONCE in the whole deck — on a body slide as a natural story beat ("then I found ${brandName}"), never as a pitch. The FINAL slide must contain NO brand name, NO CTA, NO "join us" / "follow for more" / "comment X" — just a universal quotable line.
- No hashtags, emojis, exclamation marks, em dashes.${styleLine}

OUTPUT: JSON array of exactly ${SLIDE_COUNT} strings. No markdown, no extra keys.`;
};

const normalizeStandaloneImagePrompt = (prompt: string): string => {
  return prompt
    .replace(/\brecurring\s+/gi, "")
    .replace(/\bthe same (?=(person|woman|man|character|subject|entrepreneur|founder|teacher|student)\b)/gi, "")
    .replace(/\bsame (?=(person|woman|man|character|subject|entrepreneur|founder|teacher|student)\b)/gi, "")
    .replace(/\bfrom the previous (slide|prompt)\b/gi, "")
    .replace(/\bfrom the last (slide|prompt)\b/gi, "")
    .replace(/\bfrom previous (slides|prompts)\b/gi, "")
    .replace(/\bas before\b/gi, "")
    .replace(/\bcontinuation of the previous (scene|slide|prompt)\b/gi, "")
    .replace(/\bcontinuing the previous (scene|slide|prompt)\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/^[\s,.;:-]+/, "")
    .trim();
};

// ─── Auto-pick story format ───────────────────────────────────────────────

export type StoryFormatPickResult = {
  /** Either a built-in DefaultStoryType OR a research-discovered custom format key. */
  format: StoryType;
  reason: string;
};

/**
 * Pick the best research-discovered format for a given lesson based on the
 * community's saved audience voice + buyer persona + format profile. There
 * are no built-in fallbacks — if no custom formats are saved on
 * mediaConfig, this throws with a clear "run research first" message.
 */
export const pickStoryFormat = async (
  text: string,
  communityContext?: CommunitySlideContext,
  recentFormats?: string[]
): Promise<StoryFormatPickResult> => {
  const customFormats = communityContext?.customFormats || [];
  if (customFormats.length === 0) {
    throw new Error(
      "No formats saved for this community yet. Run 🔎 Find audience language, then 🔎 Find top formats from the inspector before generating."
    );
  }

  const formatTable = customFormats
    .map((cf) => {
      const truncated = cf.structure.length > 320
        ? cf.structure.slice(0, 320) + "..."
        : cf.structure;
      return `### ${cf.key}
- Label: ${cf.label}
- Psychological lever: ${cf.lever || "(unspecified)"}
- Evidence: ${cf.evidence || "(none)"}
- Structure: ${truncated}`;
    })
    .join("\n\n");

  const customKeys = new Set(customFormats.map((cf) => cf.key));
  const communityContextNote = buildCommunityContextNote(communityContext);
  const recentNote = recentFormats?.length
    ? `\n\nRecently used formats for this community (in order): ${recentFormats.join(", ")}. Prefer a different format if the lesson allows.`
    : "";

  const profileEntries = communityContext?.formatProfile?.formats || [];
  const formatProfileNote = profileEntries.length
    ? `\n\nAUDIENCE FORMAT MIX (weighted by what wins for this audience — bias toward these unless the lesson clearly fits another):\n${profileEntries
        .map(
          (e) =>
            `- ${e.format} (${Math.round(e.weight)}% weight)${
              e.evidence ? ` — ${e.evidence}` : ""
            }`
        )
        .join("\n")}\nWeights are soft bias, not a hard rule.`
    : "";

  const systemPrompt = `You pick the best carousel format for a given lesson from the community's research-derived format library. Match the lesson's natural shape AND the audience's emotional state to the format's lever.

FORMAT LIBRARY (research-derived for this community — these are the ONLY allowed picks):

${formatTable}

DECISION PROCESS:
1. Identify the lesson's natural shape (story / steps / data / reframe / mood / etc.).
2. Read the audience voice to detect their emotional state.
3. Pick the format whose lever and structure match best.
4. Apply the audience format mix as soft bias for ties.
5. Avoid recent repeats when possible.

Return ONLY this JSON, no markdown, no commentary:
{
  "format": "<one of the format keys from the library above>",
  "reason": "one-sentence justification naming the lever, under 20 words"
}${formatProfileNote}${recentNote}`;

  logSlidePromptDev("pickStoryFormat", communityContext, systemPrompt, {
    "Recent formats hint": recentFormats?.length || 0,
  });

  const data = await createOpenAIChatCompletion<any>({
    model: "gpt-4o-mini",
    messages: [
      { role: "system", content: systemPrompt },
      {
        role: "user",
        content: `${communityContextNote}\n\nLesson text:\n"""\n${text.trim()}\n"""`,
      },
    ],
    response_format: { type: "json_object" },
  });

  const raw = data?.choices?.[0]?.message?.content?.trim() || "{}";
  let parsed: any = {};
  try {
    parsed = JSON.parse(raw);
  } catch {
    const match = raw.match(/\{[\s\S]*\}/);
    if (match) parsed = JSON.parse(match[0]);
  }
  const candidate =
    typeof parsed?.format === "string" ? parsed.format.trim() : "";
  // The classifier MUST return a custom-format key. If it returns anything
  // else (or nothing), fall back to the highest-weighted format we have.
  const fallback = customFormats[0]?.key || "";
  const format: StoryType = customKeys.has(candidate)
    ? (candidate as StoryType)
    : (fallback as StoryType);
  const reason =
    typeof parsed?.reason === "string" && parsed.reason.trim()
      ? parsed.reason.trim()
      : `Falling back to "${fallback}".`;
  return { format, reason };
};

export const generateSlideTitles = async (
  text: string,
  writingStyle: string,
  storyType: StoryType = "hard-truth",
  communityContext?: CommunitySlideContext
): Promise<string[]> => {
  const styleNote = writingStyle?.trim() ? `\nWriting style/tone override: ${writingStyle.trim()}` : "";
  const systemPrompt = buildSystemPrompt(
    storyType,
    styleNote,
    communityContext,
    communityContext?.storyPromptTemplates
  );
  const communityContextNote = buildCommunityContextNote(communityContext);

  logSlidePromptDev("generateSlideTitles", communityContext, systemPrompt, {
    storyType,
    "Resolved as custom format": Boolean(
      communityContext?.customFormats?.some((cf) => cf.key === storyType)
    ),
  });

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5",
      messages: [
        {
          role: "system",
          content: systemPrompt,
        },
        {
          role: "user",
          content: `${communityContextNote}\n\nCreate ${SLIDE_COUNT} slides that tell a connected story summarizing this content. Each slide should build on the previous one:\n\n${text}`,
        },
      ],
    });
    const raw = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const match = raw.match(/\[[\s\S]*\]/);
    const parsed = JSON.parse(match ? match[0] : raw);
    return Array.isArray(parsed) ? parsed.slice(0, SLIDE_COUNT) : [];
  } catch (error) {
    console.error("Error generating slide titles:", error);
    return [];
  }
};

export const extractImageKeyword = async (
  slideTitle: string
): Promise<string> => {
  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content:
            "Extract exactly ONE visual keyword from the slide title for finding stock photos on Unsplash. Focus on concrete, visual concepts (lifestyle, emotions, objects, nature). Return only a single word, nothing else.",
        },
        { role: "user", content: slideTitle },
      ],
      temperature: 0.3,
    });
    return data?.choices?.[0]?.message?.content?.trim() || "inspiration";
  } catch (error) {
    console.error("Error extracting keyword:", error);
    return "inspiration";
  }
};

// Pulls the "Visual direction:" block out of an elaborated format brief.
// Format briefs use the shape:
//   Voice/POV: ...
//   Visual direction: <2-4 sentences>
//   Lever: ...
// The block ends at the next "Field:" header, a blank line, or the first slide.
const extractVisualDirectionFromBrief = (brief?: string): string => {
  if (!brief || !brief.trim()) return "";
  const match = brief.match(/Visual direction:\s*([\s\S]*?)(?=\n\s*(?:[A-Z][a-zA-Z /]+:|Slide\s+\d|Anti-patterns:)|\n\s*\n|$)/i);
  if (!match) return "";
  return match[1].trim();
};

// Heuristic: does the format brief describe a graphic/illustrated treatment
// (infographic, quote card, tweet screenshot, etc.) rather than a photo? If so
// we route to the graphic-prompt branch so the model doesn't produce a photo.
const briefRequestsGraphicTreatment = (visualDirection: string): boolean => {
  if (!visualDirection) return false;
  const v = visualDirection.toLowerCase();
  const cues = [
    "graphic illustration",
    "no photos",
    "no people",
    "icon",
    "diagram",
    "chart",
    "flat vector",
    "infographic",
    "quote card",
    "quote on",
    "color block",
    "tweet card",
    "tweet screenshot",
    "screenshot",
    "typography",
    "text on color",
  ];
  return cues.some((c) => v.includes(c));
};

export const generateImagePrompt = async (
  slideTitle: string,
  globalTheme: string,
  writingStyle: string,
  storyType?: string,
  hasAuthorReference?: boolean,
  formatBrief?: string
): Promise<string> => {
  const formatVisualDirection = extractVisualDirectionFromBrief(formatBrief);
  // Prefer the per-format visual direction; community visualStyle is the fallback.
  const effectiveVisualStyle =
    formatVisualDirection || (globalTheme?.trim() || "");

  // Graphic vs photo: trust the format brief first, then fall back to the
  // legacy "infographic" key for back-compat.
  const isGraphic =
    briefRequestsGraphicTreatment(formatVisualDirection) ||
    storyType === "infographic";

  const visualStyleNote =
    !hasAuthorReference && effectiveVisualStyle
      ? `Visual direction for this format: ${effectiveVisualStyle}. Follow this aesthetic faithfully — palette, lighting, composition, mood. `
      : "";
  const writingStyleNote = !isGraphic && writingStyle?.trim()
    ? `Community writing style/tone: ${writingStyle.trim()}. Use this to guide the image mood, energy, color palette, and composition while keeping the output fully visual. `
    : "";
  const portraitNote = `${PORTRAIT_HINT} `;
  const characterConsistencyNote = hasAuthorReference
    ? `IMPORTANT: A reference image of the subject is provided. When a person appears in the scene, write "the person from the reference image" — do not describe their physical appearance (no ethnicity, skin tone, hair, age, clothing, or facial features). The reference image defines who they are and must be used to keep the character consistent across all slides. Describe only the scene, environment, lighting, composition, and mood around them — and apply the format's visual direction above to that environment.${effectiveVisualStyle ? ` Format visual direction (for the environment/mood, not the person): ${effectiveVisualStyle}.` : ""}`
    : "";

  const infographicColorNote = effectiveVisualStyle
    ? `Color palette and visual style: derive everything — background color, accent colors, lighting mood, and overall aesthetic — directly from this direction: "${effectiveVisualStyle}". Do not use generic defaults.`
    : `Color palette: clean and modern (e.g. deep navy + electric blue + white, or soft purple + gold + off-white).`;

  const systemPrompt = isGraphic
    ? `You write standalone image generation prompts for infographic illustrations. Each prompt should describe a graphic that visually explains or represents the concept in the slide caption — think diagrams, icon-based layouts, data visualizations, flow charts, or conceptual illustrations.

Choose the most fitting graphic type for the caption:
- **Bar or pie chart**: for stats, percentages, or comparisons
- **Timeline or step diagram**: for sequences, processes, or progressions
- **Icon cluster**: for lists of concepts, habits, or principles grouped visually
- **Flow chart or loop**: for cycles, cause-and-effect, or systems
- **Split comparison**: for before/after, two options, or contrasting ideas
- **Single bold visual metaphor**: one strong illustrated concept (e.g. a funnel, a ladder, a lock, a brain circuit)

Your prompt must include:
1. The graphic type and layout
2. Specific labeled elements, icons, or data points to depict (described visually — no actual text in the prompt)
3. ${infographicColorNote}
4. Rendering style consistent with the color palette — if the style calls for dark/cinematic/3D, use that instead of flat vector

Rules:
- No photos, no real people, no lifestyle scenes
- Every prompt must be fully standalone and self-contained
- 2-3 sentences max, very specific and visual
- Do NOT start with "A photo of" or any prefix`
    : `You write standalone image generation prompts for photorealistic, Pinterest-style lifestyle photos that match the mood and world of a slide caption for a specific community.

Choose the most fitting shot type for the caption's emotional tone:
- **Environmental/Ambient**: room, desk, meditation corner, studio, street, or natural setting that fits the caption world — no person needed
- **Detail/Flat lay**: journal, phone, printed page, hands, tools, objects, or artifacts that fit the caption world
- **Candid scene**: person (from behind or side, not necessarily showing face) in a believable real-life setting that fits the caption
- **POV/First-person**: looking down at hands, tools, phone, notebook, or environment from the subject's perspective
- **Cinematic wide**: wider scene with space, atmosphere, and a strong sense of place
- **App feature shot**: If the slide references an app, platform, course, or digital tool — show a close-up of a phone being held out toward someone, or a hand holding a phone with a visible app UI on screen. ${APP_VISUAL_DESCRIPTION}

Your prompt must include:
1. The shot type and composition
2. Specific props, environment, or setting details that ground it in the slide's world
3. Lighting and mood (warm golden hour, cool focused blue light, bright airy natural light, etc.)
4. If a person appears, include all visual details needed to render them in this single prompt

Rules:
- Every prompt must be fully standalone and self-contained. Assume the image model has not seen any previous prompt.
- Never refer to previous slides, previous prompts, or continuity across images.
- Never use words like "recurring", "same person", "same character", "as before", "previous slide", or "continue the scene".
- If you want a similar subject across slides, restate the full visual details inside this prompt instead of referring back.
- 2-3 sentences max, very specific and visual
- Vary the shot type to match the slide's emotional tone — not every image needs a person
- Only include props and environments implied by the caption or style notes; do not default to business or creator props unless the caption actually supports them
- If the slide text mentions an app, platform, tool, or "Digital Product University" — use the App feature shot type
- Avoid clichés (no generic "success" handshakes or stock photo poses)
- Do NOT start with "A photo of" or any prefix
${characterConsistencyNote ? `${characterConsistencyNote}\n` : ""}${visualStyleNote}${writingStyleNote}${portraitNote}`;

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5-mini",
      messages: [
        {
          role: "system",
          content: systemPrompt,
        },
        { role: "user", content: slideTitle },
      ],
    });
    const prompt =
      data?.choices?.[0]?.message?.content?.trim() ||
      `Beautiful visual scene representing: ${slideTitle}`;
    return normalizeStandaloneImagePrompt(prompt);
  } catch (error) {
    console.error("Error generating image prompt:", error);
    return normalizeStandaloneImagePrompt(`Beautiful visual scene representing: ${slideTitle}`);
  }
};

export const generateGeminiImage = async (
  geminiApiKey: string,
  prompt: string,
  referenceImages?: string[],
  personReferenceImage?: string,
  aspectRatio: "9:16" | "1:1" | "4:5" | "16:9" = "9:16"
): Promise<string> => {
  const parts: any[] = [];

  const appendImage = (refImg: string): boolean => {
    const base64Match = refImg.match(/^data:image\/(\w+);base64,(.+)$/);
    if (!base64Match) return false;
    parts.push({
      inline_data: {
        mime_type: `image/${base64Match[1]}`,
        data: base64Match[2],
      },
    });
    return true;
  };

  // Person reference goes FIRST, tagged explicitly as the subject to render.
  // Style references (mood boards, etc.) go AFTER, tagged as inspiration only.
  const personAdded = personReferenceImage ? appendImage(personReferenceImage) : false;
  const styleRefCount = (referenceImages || []).reduce(
    (count, ref) => (appendImage(ref) ? count + 1 : count),
    0
  );

  const personInstruction = personAdded
    ? `The FIRST reference image above is the person to render — match their face, identity, build, hair, and clothing style faithfully. When the prompt says "the person from the reference image", that is THIS person. Do not invent a different face. `
    : "";
  const styleRefInstruction = styleRefCount > 0
    ? `${personAdded ? `The remaining ${styleRefCount} reference image${styleRefCount === 1 ? "" : "s"}` : `The ${styleRefCount} reference image${styleRefCount === 1 ? "" : "s"} above`} are style/mood inspiration only — do not copy their subjects, only their aesthetic. `
    : "";
  const noTextRule = `IMPORTANT: Do not include any text, letters, words, numbers, typography, captions, watermarks, or writing of any kind in the image. Pure visual only.`;

  if (personAdded || styleRefCount > 0) {
    parts.push({
      text: `${personInstruction}${styleRefInstruction}Now create a new image based on this prompt: ${prompt}. ${PORTRAIT_HINT} ${noTextRule}`,
    });
  } else {
    parts.push({
      text: `${prompt}. ${PORTRAIT_HINT} ${noTextRule}`,
    });
  }

  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timeoutId = window.setTimeout(() => controller?.abort(), 120000);

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image:generateContent?key=${geminiApiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts }],
          generationConfig: {
            responseModalities: ["TEXT", "IMAGE"],
            imageConfig: { aspectRatio },
          },
        }),
        signal: controller?.signal,
      }
    );

    if (!response.ok) {
      const errText = await response.text().catch(() => response.statusText);
      throw new Error(`Gemini image API ${response.status}: ${errText}`);
    }

    const data = await response.json();
    for (const part of data?.candidates?.[0]?.content?.parts || []) {
      const inlineData = part.inline_data || part.inlineData;
      if (inlineData) {
        const mimeType = inlineData.mime_type || inlineData.mimeType;
        return `data:${mimeType};base64,${inlineData.data}`;
      }
    }
    throw new Error("No image returned from Gemini API");
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw new Error("Gemini image request timed out after 120 seconds.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const generateGptImage = async (
  prompt: string,
  referenceImages?: string[],
  personReferenceImage?: string,
  size: "1024x1024" | "1024x1536" | "1536x1024" = GPT_PORTRAIT_SIZE as "1024x1536"
): Promise<string> => {
  const styleRefs = (referenceImages || []).filter(Boolean);
  const personRef = personReferenceImage?.trim() ? personReferenceImage : "";

  const personInstruction = personRef
    ? `The FIRST reference image is the person to render — match their face, identity, build, hair, and clothing style faithfully. When the prompt says "the person from the reference image", that is THIS person. Do not invent a different face. `
    : "";
  const styleRefInstruction = styleRefs.length > 0
    ? `${personRef ? `The remaining ${styleRefs.length} reference image${styleRefs.length === 1 ? "" : "s"}` : `The ${styleRefs.length} reference image${styleRefs.length === 1 ? "" : "s"}`} are style/mood inspiration only — do not copy their subjects, only their aesthetic. `
    : "";

  // Person ref must be first so the "FIRST reference image" wording lines up.
  const refs = personRef ? [personRef, ...styleRefs] : styleRefs;
  const noTextPrompt = `${personInstruction}${styleRefInstruction}${prompt} ${PORTRAIT_HINT} IMPORTANT: Do not include any text, letters, words, numbers, typography, captions, watermarks, or writing of any kind in the image. Pure visual only.`;

  const data = refs.length
    ? await createOpenAIImageEdit<any>({
        model: "gpt-image-2",
        prompt: noTextPrompt,
        images: refs,
        n: 1,
        size,
        quality: "medium",
        output_format: "png",
      })
    : await createOpenAIImageGeneration<any>({
        model: "gpt-image-2",
        prompt: noTextPrompt,
        n: 1,
        size,
        quality: "medium",
        output_format: "png",
      });
  const b64 = data?.data?.[0]?.b64_json;
  if (!b64) throw new Error("No image returned from GPT Image API");
  return `data:image/png;base64,${b64}`;
};

export const searchUnsplashImages = async (
  unsplashAccessKey: string,
  query: string
): Promise<UnsplashImage[]> => {
  try {
    const response = await fetch(
      `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=3&orientation=portrait`,
      {
        headers: {
          Authorization: `Client-ID ${unsplashAccessKey}`,
        },
      }
    );

    if (!response.ok) throw new Error("Unsplash API request failed");

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error("Error searching Unsplash:", error);
    return [];
  }
};

export const buildOptionsFromImages = (images: UnsplashImage[]): SlideImageOption[] => {
  return images.map((img) => ({
    imageUrl: img.urls.regular,
    thumbnailUrl: img.urls.small,
    alt: img.alt_description || "Slide image",
    photographer: img.user.name,
  }));
};
